import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCompany } from '@/contexts/CompanyContext';

const DETAIL_ROUTE_REDIRECTS: Array<{ pattern: RegExp; redirectTo: string }> = [
  { pattern: /^\/customers\/[^/]+$/, redirectTo: '/customers' },
  { pattern: /^\/collections\/queue\/case\/[^/]+$/, redirectTo: '/collections/queue' },
  { pattern: /^\/collections\/queue\/create$/, redirectTo: '/collections/queue' },
  { pattern: /^\/collections\/promises\/[^/]+$/, redirectTo: '/collections/promises' },
  { pattern: /^\/collections\/promises\/create$/, redirectTo: '/collections/promises' },
  { pattern: /^\/disputes\/all\/[^/]+$/, redirectTo: '/disputes/all' },
  { pattern: /^\/payments\/allocate\/[^/]+$/, redirectTo: '/payments/receipts' },
  {
    pattern: /^\/analytics-reporting\/multi-company\/company\/[^/]+$/,
    redirectTo: '/analytics-reporting/multi-company',
  },
];

/**
 * Redirects away from detail screens when the selected company changes.
 *
 * Detail URLs contain entity IDs that belong to the previous company,
 * so staying on them after a company switch would cause "not found" errors.
 * This component navigates back to the parent list route when it detects
 * a company change while on a matching detail path.
 */
export function CompanyChangeNavigator() {
  const { selectedCompany } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const prevCompanyIdRef = useRef(selectedCompany?.id);

  useEffect(() => {
    if (prevCompanyIdRef.current !== selectedCompany?.id && prevCompanyIdRef.current !== undefined) {
      const match = DETAIL_ROUTE_REDIRECTS.find((r) => r.pattern.test(location.pathname));
      if (match) {
        navigate(match.redirectTo, { replace: true });
      }
    }

    prevCompanyIdRef.current = selectedCompany?.id;
  }, [selectedCompany?.id, navigate, location.pathname]);

  return null;
}
